import { useState, useMemo, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { Divider, IconButton, Grid, Typography, InputAdornment } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomAutocomplete from "../../shared/AutoComplete";
import { useTranslation } from "react-i18next";
import CustomDatePicker from "../../shared/CustomDate";
import dayjs, { Dayjs } from "dayjs";
import CustomTextField from "../../shared/CustomTextFeild";
import ContainedButton from "../../shared/Button/CustomContainedButton";
import OutlinedButton from "../../shared/Button/customOutlinedButton";

interface FilterPopUpBankDetailsProps {
    titleMessage: string;
    open: boolean;
    handleClose: () => void;
    montlyEndDateList: any[];
    selectBaiCode: any;
    onResetFilters: () => void;
    saveChanges: (filterData: {
        baiCode: string;
        timeFrame: string;
        monthEndDate: string | null;
        fromDate: string | null;
        toDate: string | null;
        amountMin: string | null;
        amountMax: string | null;
        checkMin: string | null;
        checkMax: string | null;
        amountAll: string | null;
        fileType: any;
    }) => void; // <-- saveChanges expects filterData argument
}


export default function FilterPopUpBankDetails(props: FilterPopUpBankDetailsProps) {
    const { open, handleClose, montlyEndDateList, saveChanges, titleMessage, selectBaiCode, onResetFilters } = props;
    const [monthEndDate, setMonthEndDate] = useState<string | null>(null);
    const [fromDate, setFromDate] = useState<Dayjs | null>(null);
    const [toDate, setToDate] = useState<Dayjs | null>(null);
    const [fromDateError, setFromDateError] = useState<string>("");
    const [toDateError, setToDateError] = useState<string>("");
    const [showAmountInputs, setShowAmountInputs] = useState(false);
    const [showCheckInputs, setShowCheckInputs] = useState(false);
    const [amountMin, setAmountMin] = useState<any>(null);
    const [amountMax, setAmountMax] = useState<any>(null);
    const [checkMin, setCheckMin] = useState<any>(null);
    const [checkMax, setCheckMax] = useState<any>(null);
    const [checkAll, setCheckAll] = useState<any>(null);
    const [amountAll, setAmountAll] = useState<any>(null);
    const [amountError, setAmountError] = useState<string>("");
    const [checkError, setCheckError] = useState<string>("");
    const [isSearchDisabled, setIsSearchDisabled] = useState(false);
    const baiCodeOptions = [
        { name: "All Types", code: "All Types" },
        { name: "All Deposits", code: "CR DET" },
        { name: "All Withdrawals", code: "DB DET" },
    ];
    const timeFrameOptions = [
        { name: "All Available Dates", code: "All Available Dates" },
        { name: "Month", code: "Month" },
        { name: "Custom Date Range", code: "Custom Date Range" },
    ];
    const downloadTypeOptions = [
        { name: "Microsoft Excel Format", code: "csv" },
        { name: "Printable Text Format", code: "txt" }
    ];
    const [selectedDownloadCode, setSelectedDownloadCode] = useState(downloadTypeOptions[0])
    const { t } = useTranslation();
    const translate = t as (key: string) => string;
    const [selectedBaiCode, setSelectedBaiCode] = useState(selectBaiCode); // Default to "All Types"
    const [selectedTimeFrameCode, setSelectedTimeFrameCode] = useState(timeFrameOptions[0]);// Default to "All Available Dates"

    const getFilterData = () => {
        return {
            baiCode: baiCodeOptions[0].code, // "All Types"
            timeFrame: timeFrameOptions[0].code, // "All Available Dates"
            monthEndDate: null,
            fromDate: null,
            toDate: null,
            amountMin: null,
            amountMax: null,
            checkMin: null,
            checkMax: null,
            amountAll: null,
            fileType: null,
        };
    };

    useEffect(() => {
        if (open) {
            setSelectedBaiCode(selectBaiCode);
        }
    }, [open, selectBaiCode]);
    
    const formatDate = (dateString: string): string => {
        return dayjs(dateString).format("MMM DD, YYYY");  
    };

    // Function to validate range (Min should be ≤ Max)
    const validateRange = () => {
        let valid = true;

        if (showAmountInputs && amountMin && amountMax && parseFloat(amountMin) > parseFloat(amountMax)) {
            setAmountError("Min amount cannot be greater than Max amount");
            valid = false;
        } else {
            setAmountError("");
        }

        if (showCheckInputs && checkMin && checkMax && parseFloat(checkMin) > parseFloat(checkMax)) {
            setCheckError("Min check# cannot be greater than Max check#");
            valid = false;
        } else {
            setCheckError("");
        }
        return valid;
    };
    const validateAll = () => {
        const isDateValid = validateDates();
        const isRangeValid = validateRange();
        if (isDateValid && isRangeValid) {
            setIsSearchDisabled(false); // Enable search
        } else {
            setIsSearchDisabled(true); // Disable search
        }
    };
    useEffect(() => {
        validateAll();
    }, [fromDate, toDate]);

    // Calculate the minimum allowed fromDate (18 months ago)
    const minAllowedDate = useMemo(() => {
        const date = new Date();
        date.setMonth(date.getMonth() - 18);
        return date.toISOString().split("T")[0]; // returns string like "2023-10-26"
    }, []);

    const resetFilters = () => {
        setMonthEndDate(null);
        setFromDate(null);
        setToDate(null);
        setFromDateError("");
        setToDateError("");
        setShowAmountInputs(false);
        setShowCheckInputs(false);
        setAmountMin(null);
        setAmountMax(null);
        setCheckMin(null);
        setCheckMax(null);
        setAmountAll(null);
        setCheckAll(null);
        setAmountError("");
        setCheckError("");
        setSelectedBaiCode(baiCodeOptions[0]); // "All Types"
        setSelectedTimeFrameCode(timeFrameOptions[0]); // "All Available Dates"
        setSelectedDownloadCode(downloadTypeOptions[0]);
    };
    // Function to handle date validation
    const validateDates = () => {
        let isValid = true;
        const minAllowedDayjs = dayjs(minAllowedDate); // Convert your string to Dayjs here 
        if (fromDate && fromDate.isBefore(minAllowedDayjs)) {
            setFromDateError("From Date must be within the last 18 months");
            isValid = false;
        } else {
            setFromDateError("");
        }

        if (toDate && fromDate && toDate.isBefore(fromDate)) {
            setToDateError("To Date cannot be earlier than From Date");
            isValid = false;
        } else {
            setToDateError("");
        }

        return isValid;
    };

    const getDefaultFilterData = () => ({
        baiCode: baiCodeOptions[0]?.code || "All Types",
        timeFrame: timeFrameOptions[0]?.code || "All Available Dates",
        monthEndDate: null,
        fromDate: null,
        toDate: null,
        amountMin: null,
        amountMax: null,
        checkMin: null,
        checkMax: null,
        amountAll: null,
        checkAll: null,
        fileType: null,
    });

    const handleDialogClose = () => {
        handleClose(); // Close the dialog
        resetFilters();
        onResetFilters();
        setSelectedBaiCode(baiCodeOptions[0])
        const defaultFilterData = getDefaultFilterData();
        saveChanges(defaultFilterData); 
    };
    const handleSearch = () => {
        const filterData = {
            baiCode: selectedBaiCode?.code,
            timeFrame: selectedTimeFrameCode?.code,
            monthEndDate,
            fromDate: fromDate ? fromDate.format("YYYY-MM-DD") : null,
            toDate: toDate ? toDate.format("YYYY-MM-DD") : null,
            amountMin,
            amountMax,
            checkMin,
            checkMax,
            amountAll,
            checkAll,
            fileType: titleMessage === "Download" ? selectedDownloadCode?.code : null
        }; 
        saveChanges(filterData); // Pass data to the parent
    };
    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth={false} // Disable predefined widths
            sx={{
                maxWidth: "700px", // Adjust width as needed
                margin: "auto", // Center horizontally
                display: "flex", // Ensure flex behavior
                alignItems: "center", // Center vertically
                justifyContent: "center", // Center horizontally
                borderRadius: 2,
                "& .MuiPaper-root": { borderRadius: "12px" },
            }}
        >
            {/* Dialog Header */}
            <DialogTitle
                sx={{
                    backgroundColor: "#059ca9",
                    color: 'white',
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '10px 10px',
                    fontWeight: "bold",
                }}
            >
                {titleMessage}
                <IconButton onClick={handleDialogClose} sx={{ color: "white" }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            {/* Dialog Content */}
            <DialogContent style={{ paddingTop: '20px' }}>
                <Grid container columnSpacing={2}>
                    {/* Type Dropdown */}
                    <Grid item xs={6}>
                        <CustomAutocomplete
                            disableClearable
                            labelText={translate("type")}
                            size="small"
                            options={baiCodeOptions}
                            value={selectedBaiCode}
                            onChange={(event, newValue) => {
                                setSelectedBaiCode(newValue);
                            }}
                            getOptionLabel={(option) => option.name}
                            isOptionEqualToValue={(option, value) => option?.code === value?.code}
                            placeholder="Select Type"
                        />
                    </Grid>

                    {/* Time Frame Dropdown */}
                    <Grid item xs={6}>
                        <CustomAutocomplete
                            disableClearable
                            labelText={translate("timeFrame")}
                            size="small"
                            options={timeFrameOptions}
                            value={selectedTimeFrameCode}
                            onChange={(event, newValue) => {
                                setSelectedTimeFrameCode(newValue);
                                if (newValue?.code === "Month") {
                                    setFromDate(null);
                                    setToDate(null);
                                    setFromDateError("");
                                    setToDateError("");
                                } else if (newValue?.code === "Custom Date Range") {
                                    setMonthEndDate(null);
                                } else {
                                    setFromDate(null);
                                    setToDate(null);
                                    setFromDateError("");
                                    setToDateError("");
                                    setMonthEndDate(null);
                                }
                            }}
                            getOptionLabel={(option) => option.name}
                            isOptionEqualToValue={(option, value) => option?.code === value?.code}
                            placeholder="Select Type"
                        />

                        {selectedTimeFrameCode?.name === "Custom Date Range" && <Typography variant="body2" sx={{ mb: 1 }}>
                            (From Date must be within the last 18 months)
                        </Typography>}
                    </Grid>

                    {/* Month-End Date Dropdown (Shown only when "Month" is selected) */}
                    {selectedTimeFrameCode?.name === "Month" && (
                        <>
                            <Grid item xs={6}></Grid>
                            <Grid item xs={6}>
                                <CustomAutocomplete
                                    disableClearable
                                    sx={{ mb: 2, mt: 1 }}
                                    options={
                                        montlyEndDateList?.map((date) => ({
                                            label: formatDate(date),  // Jun 30, 2027
                                            value: date,              // ISO string or raw date string
                                        })) || []
                                    }
                                    getOptionLabel={(option) => option.label || ""}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    freeSolo={false}
                                    value={
                                        montlyEndDateList
                                            ?.map((date) => ({
                                                label: formatDate(date),
                                                value: date,
                                            }))
                                            .find((d) => d.value === monthEndDate) || null
                                    }
                                    onChange={(event, newValue) => {
                                        setMonthEndDate(newValue?.value ?? "");
                                    }}
                                />

                            </Grid>
                        </>
                    )}
                    {/* Custom Date Range Fields (Shown when "Custom Date Range" is selected) */}
                    {selectedTimeFrameCode?.name === "Custom Date Range" && (
                        <>
                            {/* From Date */}
                            <Grid item xs={6}>
                                <CustomDatePicker
                                    labelText={translate("From Date")}
                                    value={fromDate}
                                    onChange={(newValue) => {
                                        setFromDate(newValue);
                                    }}
                                    onBlur={validateAll}
                                    error={!!fromDateError}
                                    helperText={fromDateError}
                                    size="small"
                                />
                            </Grid>
                            {/* To Date */}
                            <Grid item xs={6}>
                                <CustomDatePicker
                                    labelText={translate("To Date")}
                                    value={toDate}
                                    onChange={(newValue) => {
                                        setToDate(newValue);
                                    }}
                                    onBlur={validateAll}
                                    error={!!toDateError}
                                    helperText={toDateError} size="small"
                                />
                            </Grid>
                        </>
                    )}
                    {/* Amount Fields */}
                    <Grid item xs={6} sx={{ mt: 2 }}>
                        {!showAmountInputs ? (
                            <CustomTextField
                                id="amountAll"
                                placeholder="All"
                                labelText="Amount"
                                size="small"
                                value={amountAll}
                                onChange={(e) => {
                                    const value = e.target.value;

                                    // Remove all invalid characters except digits and dot
                                    const sanitizedValue = value.replace(/[^0-9.]/g, '');

                                    // Allow only one dot
                                    if ((sanitizedValue.match(/\./g) || []).length > 1) return;

                                    // Split into integer and decimal parts
                                    const [integerPart, decimalPart] = sanitizedValue.split(".");

                                    // Restrict integer part to 12 digits and decimal part to 2 digits
                                    if (
                                        integerPart.length <= 12 &&
                                        (!decimalPart || decimalPart.length <= 2)
                                    ) {
                                        setAmountAll(sanitizedValue);
                                    }
                                }}

                                inputProps={{
                                    inputMode: "decimal",
                                    pattern: "[0-9]*\\.?[0-9]{0,2}"
                                }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                                }}
                                sx={{ width: "100%" }}
                            />

                        ) : (
                            <CustomTextField
                                labelText="Amount"
                                placeholder="Min"
                                size="small"
                                value={amountMin}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                                }}
                                inputProps={{
                                    inputMode: "decimal",
                                    pattern: "[0-9]*\\.?[0-9]{0,2}"
                                }}
                                onChange={(e) => {
                                    const value = e.target.value;

                                    // Remove all invalid characters except digits and dot
                                    const sanitizedValue = value.replace(/[^0-9.]/g, '');

                                    // Allow only one dot
                                    if ((sanitizedValue.match(/\./g) || []).length > 1) return;

                                    // Split into integer and decimal parts
                                    const [integerPart, decimalPart] = sanitizedValue.split(".");

                                    // Restrict integer part to 12 digits and decimal part to 2 digits
                                    if (
                                        integerPart.length <= 12 &&
                                        (!decimalPart || decimalPart.length <= 2)
                                    ) {
                                        setAmountMin(sanitizedValue);
                                    }
                                }}
                                onBlur={validateAll}
                                error={!!amountError}
                                sx={{ width: "100%" }}
                            />
                        )}
                    </Grid>
                    <Grid item xs={6} sx={{ mt: 2 }}>
                        {!showAmountInputs ? (
                            <Typography
                                sx={{ fontWeight: "bold", mt: 4, color: "blue", cursor: "pointer" }}
                                onClick={() => {
                                    setShowAmountInputs(true);
                                    setAmountAll('');
                                    setAmountMin('');
                                    setAmountMax('')
                                }}
                            >
                                {` Range >>`}
                            </Typography>
                        ) : (
                            <CustomTextField
                                placeholder="Max"
                                size="small"
                                value={amountMax}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                                }}
                                inputProps={{
                                    inputMode: "decimal",
                                    pattern: "[0-9]*\\.?[0-9]{0,2}"
                                }}
                                onChange={(e) => {
                                    const value = e.target.value;

                                    // Remove all invalid characters except digits and dot
                                    const sanitizedValue = value.replace(/[^0-9.]/g, '');

                                    // Allow only one dot
                                    if ((sanitizedValue.match(/\./g) || []).length > 1) return;

                                    // Split into integer and decimal parts
                                    const [integerPart, decimalPart] = sanitizedValue.split(".");

                                    // Restrict integer part to 12 digits and decimal part to 2 digits
                                    if (
                                        integerPart.length <= 12 &&
                                        (!decimalPart || decimalPart.length <= 2)
                                    ) {
                                        setAmountMax(sanitizedValue);
                                    }
                                }}
                                onBlur={validateAll}
                                error={!!amountError}
                                helperText={amountError}
                                sx={{ width: "100%", mt: 3 }}
                            />
                        )}
                    </Grid>

                    {/* Check# Fields */}
                    <Grid item xs={6}>
                        {!showCheckInputs ? (
                            <CustomTextField
                                labelText="Check#"
                                placeholder="All"
                                size="small"
                                value={checkAll}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    // Allow only digits
                                    const sanitizedValue = value.replace(/\D/g, ''); // \D matches non-digit characters
                                    setCheckAll(sanitizedValue);
                                }}
                                inputProps={{
                                    inputMode: "decimal",
                                    pattern: "\\d*",
                                    maxLength: 60
                                }}
                            />
                        ) : (
                            <CustomTextField
                                labelText="Check#"
                                placeholder="Min"
                                size="small"
                                value={checkMin}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    // Allow only digits
                                    const sanitizedValue = value.replace(/\D/g, ''); // \D matches non-digit characters
                                    setCheckMin(sanitizedValue);
                                }}
                                inputProps={{
                                    inputMode: "decimal",
                                    pattern: "\\d*",
                                    maxLength: 60
                                }}
                                onBlur={validateAll}
                                error={!!checkError}
                            />
                        )}
                    </Grid>
                    <Grid item xs={6}>
                        {!showCheckInputs ? (
                            <Typography
                                sx={{ fontWeight: "bold", mt: 4, color: "blue", cursor: "pointer" }}
                                onClick={() => {
                                    setShowCheckInputs(true);
                                    setCheckAll(null);
                                    setCheckMin(null);
                                    setCheckMax(null);
                                }}
                            >
                                {` Range >>`}
                            </Typography>
                        ) : (
                            <CustomTextField
                                placeholder="Max"
                                size="small"
                                value={checkMax}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    // Allow only digits
                                    const sanitizedValue = value.replace(/\D/g, ''); // \D matches non-digit characters
                                    setCheckMax(sanitizedValue);
                                }}
                                inputProps={{
                                    inputMode: "decimal",
                                    pattern: "\\d*",
                                    maxLength: 60
                                }}
                                onBlur={validateAll}
                                error={!!checkError}
                                helperText={checkError}
                                sx={{ mt: 3 }}
                            />
                        )}
                    </Grid>
                    {titleMessage === "Download" && <Grid item xs={6}>
                        <CustomAutocomplete
                            disableClearable
                            labelText={translate("format")}
                            size="small"
                            options={downloadTypeOptions}
                            value={selectedDownloadCode}
                            onChange={(event, newValue) => {
                                setSelectedDownloadCode(newValue);
                            }}
                            getOptionLabel={(option) => option.name}
                            isOptionEqualToValue={(option, value) => option?.code === value?.code}
                            placeholder="Format"
                        />
                    </Grid>}
                </Grid>
            </DialogContent>
            <Divider />
            {/* Dialog Actions */}
            <DialogActions>
                <ContainedButton onClick={handleSearch}
                    disabled={isSearchDisabled}
                >
                    {titleMessage === "Download" ? "Download" : "Search"}
                </ContainedButton>
                <OutlinedButton
                    onClick={handleDialogClose}
                >
                    Cancel
                </OutlinedButton>
            </DialogActions>
        </Dialog>
    );
}
